import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingCTA from '@/components/feature/FloatingCTA';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import AnimatedStatCard from '@/components/feature/AnimatedStatCard';

// ─── Dados das 9 cidades SP ──────────────────────────────────────────────────

const SP_CITIES = [
  {
    name: 'São Paulo (Capital)',
    slug: '/sao-paulo',
    region: 'Capital',
    coordinates: { lat: -23.5505, lng: -46.6333 },
    events: 500,
    clients: 200,
    satisfaction: 98,
    sector: 'Congressos, Feiras, Eventos Multilíngues',
    venues: 'Expo Center Norte, São Paulo Expo, WTC, Rebouças',
    description: 'Maior mercado de eventos do Brasil. Atendemos feiras com 10.000+ participantes, congressos multilíngues com até 5 canais simultâneos e eventos corporativos de alto padrão.',
    imageUrl: '/img/stib/local-hotel-salao.webp',
  },
  {
    name: 'Campinas',
    slug: '/campinas',
    region: 'Região Metropolitana',
    coordinates: { lat: -22.9056, lng: -47.0608 },
    events: 200,
    clients: 100,
    satisfaction: 98,
    sector: 'Tecnologia, Inovação, Acadêmico',
    venues: 'Parque Tecnológico, Unicamp, PUC-Campinas, Expo D. Pedro',
    description: 'Polo tecnológico do interior paulista. Expertise em hackathons, summits de inovação e eventos acadêmicos com múltiplas trilhas simultâneas.',
    imageUrl: '/img/stib/setor-educacao.webp',
  },
  {
    name: 'Barueri / Alphaville',
    slug: '/barueri',
    region: 'Grande SP Oeste',
    coordinates: { lat: -23.5112, lng: -46.8764 },
    events: 180,
    clients: 120,
    satisfaction: 99,
    sector: 'Corporativo, Multinacionais, Compliance',
    venues: 'Centros empresariais Alphaville, Tamboré, hotéis corporativos',
    description: 'Maior centro empresarial da América Latina. Atendemos multinacionais com tradução simultânea, kick-offs e treinamentos corporativos de alto nível.',
    imageUrl: '/img/stib/escritorio-moderno.webp',
  },
  {
    name: 'Ribeirão Preto',
    slug: '/ribeirao-preto',
    region: 'Interior Norte',
    coordinates: { lat: -21.1767, lng: -47.8202 },
    events: 130,
    clients: 75,
    satisfaction: 97,
    sector: 'Agronegócio, Saúde, Congressos Médicos',
    venues: 'Centro de Convenções RP, RibeirãoShopping Events, hotéis',
    description: 'Capital do agronegócio e polo de saúde. Experiência comprovada em congressos médicos, feiras agrícolas e eventos científicos de grande porte.',
    imageUrl: '/img/stib/setor-medico.webp',
  },
  {
    name: 'São José dos Campos',
    slug: '/sao-jose-dos-campos',
    region: 'Vale do Paraíba',
    coordinates: { lat: -23.1791, lng: -45.8872 },
    events: 120,
    clients: 70,
    satisfaction: 99,
    sector: 'Aeroespacial, Defesa, Tecnologia',
    venues: 'Parque Tecnológico SJC, ITA, Embraer, hotéis corporativos',
    description: 'Capital da indústria aeroespacial. Atendemos o setor de defesa e tecnologia com soluções de áudio que atendem aos mais rigorosos padrões de qualidade.',
    imageUrl: '/img/stib/congresso-workshop.webp',
  },
  {
    name: 'São Bernardo do Campo',
    slug: '/sao-bernardo-do-campo',
    region: 'ABCD Paulista',
    coordinates: { lat: -23.6914, lng: -46.5647 },
    events: 110,
    clients: 65,
    satisfaction: 97,
    sector: 'Industrial, Automotivo, Metalomecânico',
    venues: 'Centros industriais ABCD, Ciesp, montadoras, auditórios',
    description: 'Berço da indústria automobilística brasileira. Equipamentos robustos para ambientes industriais, treinamentos e SIPATs com múltiplas turmas simultâneas.',
    imageUrl: '/img/stib/congresso-coffee.webp',
  },
  {
    name: 'Santos',
    slug: '/santos',
    region: 'Baixada Santista',
    coordinates: { lat: -23.9608, lng: -46.3339 },
    events: 100,
    clients: 55,
    satisfaction: 98,
    sector: 'Portuário, Logística, Shipping',
    venues: 'Centro de Convenções Santos, hotéis, terminais portuários',
    description: 'Principal cidade portuária do Brasil. Fones com isolamento premium para ambientes com ruído portuário. Expertise em treinamentos logísticos e conferências de shipping.',
    imageUrl: '/img/stib/congresso-credenciamento.webp',
  },
  {
    name: 'Guarulhos',
    slug: '/guarulhos',
    region: 'Grande SP Leste',
    coordinates: { lat: -23.4531, lng: -46.5333 },
    events: 90,
    clients: 50,
    satisfaction: 96,
    sector: 'Logística, Aeroportuário, Industrial',
    venues: 'Hotéis aeroporto, centros logísticos, GRU Airport',
    description: 'Segunda maior cidade de SP e hub logístico estratégico. Proximidade do aeroporto internacional facilita eventos com participantes de todo o Brasil e exterior.',
    imageUrl: '/img/stib/local-convencoes-int.webp',
  },
  {
    name: 'Jundiaí',
    slug: '/jundiai',
    region: 'Interior Oeste',
    coordinates: { lat: -23.1865, lng: -46.8845 },
    events: 80,
    clients: 45,
    satisfaction: 98,
    sector: 'Logística, Distribuição, Empresarial',
    venues: 'Centros de distribuição, parques empresariais, hotéis',
    description: 'Polo logístico estratégico entre SP e Campinas. Ideal para treinamentos em centros de distribuição e eventos empresariais regionais.',
    imageUrl: '/img/stib/congresso-plenaria.webp',
  },
];

// ─── Totals ──────────────────────────────────────────────────────────────────

const TOTAL_EVENTS = SP_CITIES.reduce((sum, c) => sum + c.events, 0);
const TOTAL_CLIENTS = SP_CITIES.reduce((sum, c) => sum + c.clients, 0);
const AVG_SATISFACTION = Math.round(SP_CITIES.reduce((sum, c) => sum + c.satisfaction, 0) / SP_CITIES.length);

// ─── FAQ Item ────────────────────────────────────────────────────────────────

function FAQItem({ question, answer, index }: { question: string; answer: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden" itemScope itemType="https://schema.org/Question">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-7 py-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
        aria-expanded={open}
        id={`faq-spestado-${index}`}
      >
        <h4 className="font-body font-semibold text-secondary text-base leading-snug pr-4" itemProp="name">{question}</h4>
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-primary/10">
          <i className={`ri-${open ? 'subtract' : 'add'}-line text-primary text-base`}></i>
        </div>
      </button>
      {open && (
        <div className="px-7 pb-6 bg-white" itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
          <p className="font-body text-gray-600 text-sm leading-relaxed" itemProp="text">{answer}</p>
        </div>
      )}
    </div>
  );
}

// ─── FAQs ────────────────────────────────────────────────────────────────────

const FAQS = [
  {
    question: 'A STIB atende eventos em múltiplas cidades de São Paulo simultaneamente?',
    answer: 'Sim! Nossa estrutura no estado de São Paulo permite atender eventos simultâneos em diferentes cidades. Com equipes distribuídas estrategicamente e estoque dimensionado para 1.500+ participantes simultâneos, conseguimos cobrir eventos paralelos em Campinas, Barueri, São José dos Campos e outras cidades no mesmo dia.',
  },
  {
    question: 'Qual a diferença de preço entre eventos na capital e no interior?',
    answer: 'O custo do equipamento é o mesmo em todo o estado. A única variação é o deslocamento da equipe técnica, que é cotado individualmente conforme a distância. Cidades mais próximas da capital (Barueri, Guarulhos, ABCD) têm custo logístico reduzido. Para cidades como Ribeirão Preto e São José dos Campos, o deslocamento é calculado de forma transparente no orçamento.',
  },
  {
    question: 'Vocês têm técnicos locais em cada região de SP?',
    answer: 'Nossa equipe técnica está baseada na capital, mas mantemos técnicos parceiros e pontos de apoio logístico em Campinas, São José dos Campos e Ribeirão Preto. Isso garante tempos de resposta mais rápidos e reduz custos de deslocamento para eventos nessas regiões.',
  },
  {
    question: 'Qual o prazo para confirmar um evento fora da capital?',
    answer: 'O prazo é o mesmo da capital: 3 dias úteis para eventos pequenos (até 100 pessoas) e 7 a 10 dias para eventos maiores. Para cidades a mais de 200km de SP, recomendamos adicionar 2 dias extras para planejamento logístico.',
  },
  {
    question: 'Uma mesma empresa pode contratar para eventos em cidades diferentes com contrato único?',
    answer: 'Sim! Oferecemos contratos corporativos unificados. Empresas com operação em múltiplas cidades do estado podem fechar um contrato guarda-chuva com condições especiais, simplificando a gestão de eventos recorrentes em diferentes praças.',
  },
  {
    question: 'Qual a capacidade máxima de participantes para um evento no interior de SP?',
    answer: 'Nossa capacidade é dimensionada por evento e por cidade. Na capital, atendemos até 5.000 participantes. Para Campinas e região, até 1.500. Para as demais cidades do interior, até 800 participantes simultâneos. Todos com equipamentos premium e backup completo.',
  },
];

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function SaoPauloEstadoHubPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: overviewRef, isVisible: overviewVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: compareRef, isVisible: compareVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.1 });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

  useEffect(() => {
    document.title = 'Palestra Silenciosa Estado de São Paulo | Cobertura em 9 Cidades | STIB';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Palestra silenciosa no estado de São Paulo: cobertura completa em 9 cidades — São Paulo Capital, Campinas, Barueri, SJC, Ribeirão Preto, Santos, ABCD, Guarulhos e Jundiaí. 500+ eventos. Compare a cobertura!'
      );
    }

    const geoPos = document.querySelector('meta[name="geo.position"]');
    if (geoPos) geoPos.setAttribute('content', '-23.5505;-46.6333');
    const geoReg = document.querySelector('meta[name="geo.region"]');
    if (geoReg) geoReg.setAttribute('content', 'BR-SP');
    const geoPlace = document.querySelector('meta[name="geo.placename"]');
    if (geoPlace) geoPlace.setAttribute('content', 'São Paulo');

    const schemas = [
      {
        id: 'spestado-organization',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'STIB Palestra Silenciosa São Paulo',
          description: 'Cobertura completa de palestra silenciosa no estado de São Paulo — 9 cidades, 500+ eventos realizados.',
          url: `${siteUrl}/sao-paulo-estado`,
          telephone: '+55-11-91777-6227',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'São Paulo',
            addressRegion: 'SP',
            addressCountry: 'BR',
          },
          areaServed: {
            '@type': 'State',
            name: 'São Paulo',
            containsPlace: SP_CITIES.map((c) => ({
              '@type': 'City',
              name: c.name,
              geo: { '@type': 'GeoCoordinates', latitude: c.coordinates.lat, longitude: c.coordinates.lng },
            })),
          },
        },
      },
      {
        id: 'spestado-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Palestra Silenciosa no Estado de São Paulo', item: `${siteUrl}/sao-paulo-estado` },
          ],
        },
      },
      {
        id: 'spestado-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [siteUrl]);

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-[85vh] w-full flex items-center justify-center overflow-hidden"
        aria-label="Palestra Silenciosa no Estado de São Paulo — Hub"
      >
        <div className="absolute inset-0">
          <img
            src="/img/stib/cidade-sao-paulo.webp"
            alt="Mapa do Estado de São Paulo — Cobertura Palestra Silenciosa SP"
            title="Cobertura Palestra Silenciosa no Estado de São Paulo"
            className={`w-full h-full object-cover object-center transition-all duration-1200 ${heroVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/75"></div>
        </div>

        <div className="absolute top-24 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <Breadcrumb
              variant="light"
              items={[
                { label: 'Home', to: '/' },
                { label: 'Cidades', to: '/cidades' },
                { label: 'Estado de São Paulo' },
              ]}
            />
          </div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <div className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-2-line text-primary text-sm"></i>
            </div>
            9 cidades · cobertura estadual
          </div>

          <header>
            <h1 className={`font-heading font-extrabold text-5xl md:text-8xl text-white mb-6 tracking-tight leading-none lowercase transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              palestra silenciosa<br />
              <span className="text-primary">estado de são paulo</span>
            </h1>
          </header>

          <p className={`font-body font-light text-xl md:text-2xl text-white/85 mb-4 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            Cobertura completa em <strong className="text-white">9 polos estratégicos</strong> do estado.
            Da capital ao interior, do porto ao Vale do Paraíba — a mesma tecnologia premium,
            a mesma excelência técnica.
          </p>

          <div className={`flex items-center justify-center gap-3 mb-10 transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="font-body text-primary text-lg font-semibold">{TOTAL_EVENTS.toLocaleString()}+ eventos</span>
            <span className="text-white/40">·</span>
            <span className="font-body text-white/80 text-lg">{TOTAL_CLIENTS.toLocaleString()}+ clientes</span>
            <span className="text-white/40">·</span>
            <span className="font-body text-white/80 text-lg">{AVG_SATISFACTION}% satisfação</span>
          </div>

          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-arrow-down-line text-white text-lg"></i>
              </div>
            </a>
            <a
              href="https://wa.me/5511917776227?text=Olá! Preciso de orçamento para palestra silenciosa no estado de São Paulo."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              </div>
              WhatsApp SP
            </a>
          </div>
        </div>
      </section>

      {/* ── STATS OVERVIEW ──────────────────────────────────── */}
      <section ref={statsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Números do Estado de São Paulo
            </span>
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              o maior mercado de eventos do brasil em números
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { numericValue: TOTAL_EVENTS, suffix: '+', label: 'Eventos no Estado', icon: 'ri-calendar-event-line' },
              { numericValue: 9, suffix: '', label: 'Cidades Cobertas', icon: 'ri-map-pin-2-line' },
              { numericValue: TOTAL_CLIENTS, suffix: '+', label: 'Empresas Atendidas', icon: 'ri-briefcase-line' },
              { numericValue: AVG_SATISFACTION, suffix: '%', label: 'Satisfação Média', icon: 'ri-star-line' },
            ].map((s, i) => (
              <AnimatedStatCard
                key={i}
                numericValue={s.numericValue}
                suffix={s.suffix}
                label={s.label}
                icon={s.icon}
                delay={i * 100}
                visible={statsVisible}
                numberColor="text-secondary"
                className="p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5"
              />
            ))}
          </div>

          <div className={`mt-12 text-center transition-all duration-700 delay-500 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <p className="font-body text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">
              <strong className="text-secondary">Dados consolidados de 2019–2026.</strong> Cobertura em todo o estado: Grande SP, ABCD, Vale do Paraíba, Região Metropolitana de Campinas, Baixada Santista e Interior.
            </p>
          </div>
        </div>
      </section>

      {/* ── REGIONAL OVERVIEW ────────────────────────────────── */}
      <section ref={overviewRef} className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Cobertura estratégica
            </span>
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${overviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              7 regiões, 9 polos, 1 padrão de excelência
            </h2>
            <p className="font-body text-lg text-gray-600 mt-6 max-w-3xl mx-auto leading-relaxed">
              Cada região do estado tem seu próprio perfil de eventos. Conhecemos profundamente cada uma delas
              e adaptamos nossa operação para entregar o mesmo nível de qualidade em qualquer cidade.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-building-4-line', region: 'Capital', cities: 'São Paulo', highlight: 'Grandes feiras, congressos multilíngues, eventos internacionais', color: 'from-primary/15 to-primary/5' },
              { icon: 'ri-building-line', region: 'Grande SP Oeste', cities: 'Barueri / Alphaville', highlight: 'Multinacionais, eventos corporativos, compliance', color: 'from-secondary/10 to-secondary/5' },
              { icon: 'ri-cpu-line', region: 'Região Metropolitana', cities: 'Campinas', highlight: 'Tecnologia, inovação, hackathons, Unicamp', color: 'from-accent/15 to-accent/5' },
              { icon: 'ri-plane-line', region: 'Vale do Paraíba', cities: 'São José dos Campos', highlight: 'Aeroespacial, defesa, ITA, Embraer', color: 'from-primary/10 to-secondary/10' },
              { icon: 'ri-plant-line', region: 'Interior Norte', cities: 'Ribeirão Preto', highlight: 'Agronegócio, congressos médicos, Agrishow', color: 'from-accent/10 to-primary/5' },
              { icon: 'ri-ship-line', region: 'Baixada Santista', cities: 'Santos', highlight: 'Portuário, shipping, logística marítima', color: 'from-secondary/15 to-accent/5' },
              { icon: 'ri-tools-line', region: 'ABCD Paulista', cities: 'SBC + Guarulhos + Jundiaí', highlight: 'Indústria, logística, centros de distribuição', color: 'from-primary/5 to-secondary/15' },
            ].map((r, i) => (
              <div
                key={i}
                className={`bg-gradient-to-br ${r.color} rounded-2xl p-7 transition-all duration-500 ${overviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/70 mb-5">
                  <i className={`${r.icon} text-secondary text-xl`}></i>
                </div>
                <h3 className="font-heading font-bold text-secondary text-lg mb-1">{r.region}</h3>
                <p className="font-body text-sm text-primary font-semibold mb-3">{r.cities}</p>
                <p className="font-body text-sm text-gray-500 leading-relaxed">{r.highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARATIVO DE COBERTURA (TABLE) ────────────────── */}
      <section ref={compareRef} className="py-24 bg-white" aria-label="Comparativo de cobertura — 9 cidades SP">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Comparativo completo
            </span>
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${compareVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              cobertura por cidade
            </h2>
            <p className="font-body text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
              Compare a nossa presença em cada polo estratégico do estado de São Paulo.
            </p>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block overflow-hidden rounded-3xl border border-gray-100">
            <div className={`transition-all duration-700 ${compareVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <table className="w-full" role="table" aria-label="Comparativo de cobertura por cidade no estado de São Paulo">
                <thead>
                  <tr className="bg-secondary text-white">
                    <th className="text-left px-6 py-5 font-heading text-sm uppercase tracking-wider">Cidade</th>
                    <th className="text-center px-4 py-5 font-heading text-sm uppercase tracking-wider">Região</th>
                    <th className="text-center px-4 py-5 font-heading text-sm uppercase tracking-wider">Eventos</th>
                    <th className="text-center px-4 py-5 font-heading text-sm uppercase tracking-wider">Clientes</th>
                    <th className="text-center px-4 py-5 font-heading text-sm uppercase tracking-wider">Satisfação</th>
                    <th className="text-left px-6 py-5 font-heading text-sm uppercase tracking-wider">Setor Principal</th>
                    <th className="text-center px-4 py-5 font-heading text-sm uppercase tracking-wider">Ver Página</th>
                  </tr>
                </thead>
                <tbody>
                  {SP_CITIES.map((city, i) => (
                    <tr
                      key={city.slug}
                      className={`border-b border-gray-50 transition-colors hover:bg-gray-50/70 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                    >
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                            <i className="ri-map-pin-line text-primary text-lg"></i>
                          </div>
                          <span className="font-body font-semibold text-secondary text-sm">{city.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-5 text-center">
                        <span className="inline-block bg-secondary/5 text-secondary text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">{city.region}</span>
                      </td>
                      <td className="px-4 py-5 text-center">
                        <span className="font-body font-bold text-secondary text-sm">{city.events}+</span>
                      </td>
                      <td className="px-4 py-5 text-center">
                        <span className="font-body text-gray-600 text-sm">{city.clients}+</span>
                      </td>
                      <td className="px-4 py-5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all duration-1000"
                              style={{ width: `${city.satisfaction}%` }}
                            ></div>
                          </div>
                          <span className="font-body font-bold text-secondary text-xs">{city.satisfaction}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="font-body text-gray-500 text-sm">{city.sector}</span>
                      </td>
                      <td className="px-4 py-5 text-center">
                        <Link
                          to={city.slug}
                          className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-full font-body text-xs font-semibold hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                        >
                          Ver página
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-right-line text-white text-xs"></i>
                          </div>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-secondary/5">
                    <td className="px-6 py-5">
                      <span className="font-body font-bold text-secondary text-sm">TOTAL / MÉDIA</span>
                    </td>
                    <td className="px-4 py-5 text-center">
                      <span className="font-body font-bold text-secondary text-sm">7 regiões</span>
                    </td>
                    <td className="px-4 py-5 text-center">
                      <span className="font-body font-bold text-primary text-sm">{TOTAL_EVENTS.toLocaleString()}+</span>
                    </td>
                    <td className="px-4 py-5 text-center">
                      <span className="font-body font-bold text-primary text-sm">{TOTAL_CLIENTS.toLocaleString()}+</span>
                    </td>
                    <td className="px-4 py-5 text-center">
                      <span className="font-body font-bold text-primary text-sm">{AVG_SATISFACTION}%</span>
                    </td>
                    <td className="px-6 py-5"></td>
                    <td className="px-4 py-5 text-center"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Mobile Cards (instead of table) */}
          <div className="lg:hidden space-y-4">
            {SP_CITIES.map((city, i) => (
              <div
                key={city.slug}
                className={`bg-white rounded-2xl border border-gray-100 p-5 transition-all duration-500 ${compareVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10">
                      <i className="ri-map-pin-line text-primary text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-secondary text-sm">{city.name}</h3>
                      <span className="font-body text-xs text-gray-400">{city.region}</span>
                    </div>
                  </div>
                  <Link
                    to={city.slug}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 hover:bg-primary/20 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-primary text-sm"></i>
                  </Link>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="font-body font-bold text-secondary text-lg">{city.events}+</p>
                    <p className="font-body text-xs text-gray-400">Eventos</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="font-body font-bold text-secondary text-lg">{city.clients}+</p>
                    <p className="font-body text-xs text-gray-400">Clientes</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="font-body font-bold text-secondary text-lg">{city.satisfaction}%</p>
                    <p className="font-body text-xs text-gray-400">Satisfação</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-briefcase-line text-gray-400 text-sm"></i>
                  </div>
                  <span className="font-body text-xs text-gray-500">{city.sector}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CITY CARDS GRID ─────────────────────────────────── */}
      <section ref={cardsRef} className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Explore cada polo
            </span>
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              9 cidades, uma referência
            </h2>
            <p className="font-body text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
              Clique em qualquer cidade para ver a página dedicada com informações completas sobre cobertura, venues, cases e orçamento.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SP_CITIES.map((city, i) => (
              <Link
                key={city.slug}
                to={city.slug}
                className={`group bg-white rounded-3xl border border-gray-100 overflow-hidden transition-all duration-500 hover:border-primary/30 cursor-pointer ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={city.imageUrl}
                    alt={`Palestra silenciosa ${city.name} — STIB`}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent"></div>
                  <div className="absolute bottom-4 left-5">
                    <h3 className="font-heading font-bold text-white text-lg leading-tight">{city.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-map-pin-line text-primary text-xs"></i>
                      </div>
                      <span className="font-body text-white/80 text-xs">{city.region}</span>
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <p className="font-body text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">{city.description}</p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="font-heading font-bold text-secondary text-base">{city.events}+</p>
                        <p className="font-body text-xs text-gray-400">eventos</p>
                      </div>
                      <div className="w-px h-8 bg-gray-200"></div>
                      <div className="text-center">
                        <p className="font-heading font-bold text-secondary text-base">{city.clients}+</p>
                        <p className="font-body text-xs text-gray-400">clientes</p>
                      </div>
                      <div className="w-px h-8 bg-gray-200"></div>
                      <div className="text-center">
                        <p className="font-heading font-bold text-primary text-base">{city.satisfaction}%</p>
                        <p className="font-body text-xs text-gray-400">satisfação</p>
                      </div>
                    </div>

                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary group-hover:text-white transition-all duration-300 flex-shrink-0">
                      <i className="ri-arrow-right-line text-primary group-hover:text-white text-base transition-colors"></i>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────── */}
      <section ref={ctaRef} className="relative py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/img/stib/cidade-sao-paulo.webp"
            alt="Orçamento palestra silenciosa estado de São Paulo"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary/88 via-secondary/75 to-secondary/55"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className={`max-w-2xl transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-white lowercase leading-tight mb-6">
                pronto para levar palestra silenciosa<br />
                para qualquer cidade de são paulo?
              </h2>
              <p className="font-body text-lg text-white/80 leading-relaxed">
                <strong className="text-white">500+ eventos em 9 cidades.</strong> Da capital ao interior,
                a mesma qualidade premium e o mesmo suporte técnico dedicado. Solicite seu orçamento
                e descubra como podemos transformar seu evento em qualquer ponto do estado.
              </p>
            </div>

            <div className={`flex flex-col gap-4 flex-shrink-0 transition-all duration-700 delay-200 ${ctaVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <a
                href="/orcamento"
                className="inline-flex items-center justify-center gap-3 bg-primary text-white px-12 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                SOLICITAR ORÇAMENTO
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-arrow-up-line text-white text-lg"></i>
                </div>
              </a>
              <a
                href="https://wa.me/5511917776227?text=Olá! Preciso de orçamento para palestra silenciosa no estado de São Paulo."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-white text-secondary px-12 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-green-500 text-xl"></i>
                </div>
                FALAR NO WHATSAPP
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────── */}
      <section
        ref={faqRef}
        className="py-24 bg-white"
        aria-label="Perguntas frequentes palestra silenciosa estado de São Paulo"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Dúvidas Frequentes
            </span>
            <h2 className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              faq — estado de são paulo
            </h2>
          </div>

          <div className={`space-y-4 transition-all duration-700 delay-200 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {FAQS.map((f, i) => (
              <FAQItem key={i} question={f.question} answer={f.answer} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}